/**
 * Notification Tracker — Unread notification ID tracking + lastNotificationTime persistence
 *
 * Matches Xamarin:
 * - _applicationContentService.UnreadNotificationTaskIdList: tracks task IDs from SignalR pushes
 * - _preferencesService.GetLastNotificationTime / SetLastNotificationTime: persists read marker
 *
 * Used by:
 * - SignalR hook: adds task IDs on push → drives bell badge count
 * - NotificationPopupDialog: reads lastNotificationTime for isNew, saves on close, clears unreads
 * - Notification query hooks: uses unread count to determine fetch limit
 */

const LAST_NOTIFICATION_TIME_KEY = 'yms_last_notification_time';

// ─────────────────────────────────────────────────────────────────
// Unread Notification Task IDs (in-memory, matches Xamarin UnreadNotificationTaskIdList)
// ─────────────────────────────────────────────────────────────────

const unreadTaskIds = new Set<string>();

/** Matches Xamarin SendNotificationUpdate — add task ID if not already present.
 *  Returns true only when the ID is genuinely new (first alert for this task). */
export function addUnreadNotificationId(taskId: string): boolean {
  if (unreadTaskIds.has(taskId)) return false;
  unreadTaskIds.add(taskId);
  return true;
}

/** Get current unread count (matches Xamarin UnreadNotificationTaskIdList.Count) */
export function getUnreadCount(): number {
  return unreadTaskIds.size;
}

/** Clear all unread IDs (matches Xamarin Destroy → UnreadNotificationTaskIdList.Clear()) */
export function clearUnreadNotificationIds(): void {
  unreadTaskIds.clear();
}

// ─────────────────────────────────────────────────────────────────
// Last Notification Time (localStorage, matches Xamarin PreferencesService)
// ─────────────────────────────────────────────────────────────────

/**
 * Get the last notification time from localStorage.
 * Matches Xamarin _preferencesService.GetLastNotificationTime()
 * Returns null if not set.
 */
export function getLastNotificationTime(): string | null {
  try {
    return localStorage.getItem(LAST_NOTIFICATION_TIME_KEY);
  } catch {
    return null;
  }
}

/**
 * Save the last notification time to localStorage.
 * Matches Xamarin _preferencesService.SetLastNotificationTime(NotificationList[0]?.UpdatedAt)
 * Called when the notification popup/page is closed (Xamarin Destroy).
 */
export function setLastNotificationTime(isoDate: string): void {
  try {
    localStorage.setItem(LAST_NOTIFICATION_TIME_KEY, isoDate);
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

/** Clear last notification time (used on logout) */
export function clearLastNotificationTime(): void {
  try {
    localStorage.removeItem(LAST_NOTIFICATION_TIME_KEY);
  } catch {
    // Silently fail
  }
}
